import {
  Alert,
  Button,
  Card,
  CardBody,
  Dropdown,
  EmptyState,
  formatDate,
  formatRelative,
  IconButton,
  Inline,
  Money,
  Pagination,
  SearchInput,
  Select,
  Skeleton,
  Stack,
  SummaryStat,
  SummaryStats,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableLoading,
  TableRow,
  Text,
  Toolbar,
  ToolbarCount,
  ToolbarItem,
  ToolbarSpacer,
  UploadStatusBadge,
  VisuallyHidden,
  type DropdownItem,
} from '@hkdevs/design-system';
import {
  usePendingSummary,
  useUploads,
  type PendingPaySummary,
  type UploadStatus,
  type UploadSummary,
} from '../../api/queries/uploads';
import { dollars } from '../lineItems/LineItemList';
import { useStatusAnnouncer } from './announcer';
import { downloadLabel, isLegacy, LegacyBadge } from './legacyUpload';

export const PAGE_SIZE = 25;

/** The page's URL state (ADR 0023): the search, the status and the page. */
export interface PayDataPendingSearch {
  q?: string | undefined;
  status?: UploadStatus | undefined;
  /** 1-based; left out for the first. */
  page?: number | undefined;
}

const STATUSES: { value: 'all' | UploadStatus; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'processing', label: 'Processing' },
  { value: 'queued', label: 'Queued' },
  { value: 'rejected', label: 'Rejected' },
];

export function parsePayDataPendingSearch(search: Record<string, unknown>): PayDataPendingSearch {
  const status = STATUSES.find((s) => s.value !== 'all' && s.value === search.status);
  const page = Number(search.page);
  return {
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    status: status ? (status.value as UploadStatus) : undefined,
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;

export interface PendingPayUploadsViewProps {
  search: PayDataPendingSearch;
  onSearchChange: (next: PayDataPendingSearch) => void;
  /** Opens Clear pending pay with the summary it clears. */
  onClearPending: (summary: PendingPaySummary) => void;
  onDownload: (upload: UploadSummary) => void;
  onOpenErrorReport: (upload: UploadSummary) => void;
  onDelete: (upload: UploadSummary) => void;
  /** Status changes are announced here; off while a dialog announces its own. */
  announce?: boolean;
}

/** The admin's Pending pay uploads page body (pc-dbb.13): the summary and the uploads. */
export function PendingPayUploadsView({
  search,
  onSearchChange,
  onClearPending,
  onDownload,
  onOpenErrorReport,
  onDelete,
  announce = true,
}: PendingPayUploadsViewProps) {
  const summary = usePendingSummary();
  const page = search.page ?? 1;
  const q = search.q?.trim() || undefined;
  const uploads = useUploads({
    kind: 'pending',
    q,
    status: search.status,
    page,
    pageSize: PAGE_SIZE,
  });
  const announcement = useStatusAnnouncer(uploads.data?.items);
  const filtered = !!(q || search.status);
  /** New filters go back to the first page. */
  const set = (patch: Partial<PayDataPendingSearch>) =>
    onSearchChange({ ...search, ...patch, page: undefined });

  if (summary.isError) {
    return (
      <Alert
        tone="danger"
        title="Pending pay couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void summary.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  }
  if (!summary.data) {
    return (
      <>
        <SummaryStats aria-hidden="true">
          <SummaryStat label="Pending pay date" value={<Skeleton width="8rem" />} size="lg" />
          <SummaryStat label="Records" value={<Skeleton width="4rem" />} />
          <SummaryStat label="Total" value={<Skeleton width="6rem" />} />
          <SummaryStat label="Reps affected" value={<Skeleton width="3rem" />} />
        </SummaryStats>
        <VisuallyHidden role="status">Loading pending pay</VisuallyHidden>
      </>
    );
  }

  const current = summary.data;
  // Nothing pending and nothing uploaded for it: the empty page.
  if (!current.active && current.uploads === 0) {
    return (
      <>
        <SummaryStats
          aria-label="Pending pay"
          empty={{
            title: 'No pending pay loaded',
            description:
              'Uploading files sets the pending pay date. Until it’s cleared, every file you upload here applies to that date.',
          }}
        />
        <Card>
          <CardBody>
            <EmptyState
              title="Nothing uploaded yet"
              description="Upload files to set the pending pay date."
              icon="upload"
              headingLevel={2}
            />
          </CardBody>
        </Card>
      </>
    );
  }

  let body;
  if (uploads.isError) {
    body = (
      <Alert
        tone="danger"
        title="Uploads couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void uploads.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  } else if (uploads.data?.total === 0) {
    body = (
      <Card>
        <CardBody>
          {filtered ? (
            <EmptyState
              title="No matching uploads"
              description="No uploads match your search or status filter."
              icon="search"
              headingLevel={2}
              actions={
                <Button size="sm" variant="secondary" onClick={() => onSearchChange({})}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="Nothing uploaded yet"
              description="Every file uploaded for this date was deleted. Upload files to add to it."
              icon="upload"
              headingLevel={2}
            />
          )}
        </CardBody>
      </Card>
    );
  } else {
    const data = uploads.data;
    const pageCount = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
    body = (
      <>
        <Card flush>
          <Table
            aria-label="Uploads"
            responsive="cards"
            busy={!data || (uploads.isFetching && uploads.isPlaceholderData)}
          >
            <TableHead>
              <TableCell>File name</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell numeric>Records</TableCell>
              <TableCell numeric>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>
                <VisuallyHidden>Actions</VisuallyHidden>
              </TableCell>
            </TableHead>
            <TableBody>
              {!data ? (
                <TableLoading colSpan={6} rows={4} label="Loading uploads" />
              ) : (
                data.items.map((upload) => (
                  <PendingRow
                    key={upload.id}
                    upload={upload}
                    onDownload={onDownload}
                    onOpenErrorReport={onOpenErrorReport}
                    onDelete={onDelete}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </Card>
        {data && pageCount > 1 && (
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={(next) =>
              onSearchChange({ ...search, page: next > 1 ? next : undefined })
            }
            size="sm"
          />
        )}
      </>
    );
  }

  return (
    <>
      {current.active && current.payDate ? (
        <SummaryStats
          aria-label="Pending pay"
          actions={
            <Button variant="danger" size="sm" onClick={() => onClearPending(current)}>
              Clear pending pay
            </Button>
          }
          footer={
            current.lastUpdatedAt ? `Last updated ${formatRelative(current.lastUpdatedAt)}` : null
          }
        >
          <SummaryStat label="Pending pay date" value={formatDate(current.payDate)} size="lg" />
          <SummaryStat label="Records" value={current.records.toLocaleString('en-US')} />
          <SummaryStat label="Total" value={<Money value={dollars(current.totalCents)} />} />
          <SummaryStat label="Reps affected" value={current.reps.toLocaleString('en-US')} />
        </SummaryStats>
      ) : (
        <SummaryStats
          aria-label="Pending pay"
          empty={{
            title: 'No pending pay loaded',
            description: 'Upload files to set the pending pay date.',
          }}
        />
      )}
      <Stack gap={3} shrink>
        <Toolbar aria-label="Filter uploads">
          <ToolbarItem size="search">
            <SearchInput
              label="Search uploads by file name"
              placeholder="Search by file name"
              value={search.q ?? ''}
              onValueChange={(next) => set({ q: next || undefined })}
            />
          </ToolbarItem>
          <ToolbarItem size="filter">
            <Select
              aria-label="Status"
              options={STATUSES}
              value={search.status ?? 'all'}
              onChange={(event) => {
                const value = event.target.value as 'all' | UploadStatus;
                set({ status: value === 'all' ? undefined : value });
              }}
            />
          </ToolbarItem>
          <ToolbarSpacer />
          {uploads.data && (
            <ToolbarCount>{plural(uploads.data.total, 'upload', 'uploads')}</ToolbarCount>
          )}
        </Toolbar>
        {body}
      </Stack>
      <VisuallyHidden role="status">{announce ? announcement : ''}</VisuallyHidden>
    </>
  );
}

function PendingRow({
  upload,
  onDownload,
  onOpenErrorReport,
  onDelete,
}: {
  upload: UploadSummary;
  onDownload: (upload: UploadSummary) => void;
  onOpenErrorReport: (upload: UploadSummary) => void;
  onDelete: (upload: UploadSummary) => void;
}) {
  const rejected = upload.status === 'rejected';
  const completed = upload.status === 'completed';
  const menu: DropdownItem[] = rejected
    ? [{ label: 'View error report', icon: 'info', onClick: () => onOpenErrorReport(upload) }]
    : completed
      ? [{ label: 'Delete upload', icon: 'trash', danger: true, onClick: () => onDelete(upload) }]
      : [];
  const status = (
    <UploadStatusBadge
      status={upload.status}
      {...(rejected && {
        onRejectedClick: () => onOpenErrorReport(upload),
        rejectedActionLabel: `Rejected, view the error report for ${upload.fileName}`,
      })}
    />
  );
  return (
    <TableRow>
      <TableCell rowHeader tone="mono">
        {upload.fileName}
      </TableCell>
      <TableCell label="Uploaded">
        <Stack as="span" gap={0}>
          <span>{formatDate(upload.uploadedAt, 'uploaded')}</span>
          <Text tone="muted" size="meta">
            {upload.uploadedBy.name}
          </Text>
        </Stack>
      </TableCell>
      <TableCell label="Records" numeric tone="muted">
        {upload.records === null ? '—' : upload.records.toLocaleString('en-US')}
      </TableCell>
      <TableCell label="Total" numeric tone="strong">
        {upload.totalCents === null ? '—' : <Money value={dollars(upload.totalCents)} />}
      </TableCell>
      <TableCell label="Status">
        {isLegacy(upload) ? (
          <Inline as="span" gap={1}>
            {status}
            <LegacyBadge />
          </Inline>
        ) : (
          status
        )}
      </TableCell>
      <TableCell>
        <Inline as="span" gap={1} justify="end" wrap={false}>
          {completed && (
            <IconButton
              icon="download"
              size="sm"
              variant="ghost"
              aria-label={downloadLabel(upload)}
              onClick={() => onDownload(upload)}
            />
          )}
          {menu.length > 0 && (
            <Dropdown
              align="end"
              trigger={
                <IconButton
                  icon="more-vertical"
                  size="sm"
                  variant="ghost"
                  aria-label={`More actions for ${upload.fileName}`}
                />
              }
              items={menu}
            />
          )}
        </Inline>
      </TableCell>
    </TableRow>
  );
}
